import { readonly } from 'vue';
import { useStorage } from '@vueuse/core';

const STORAGE_KEY = 'qbeek:partner-referral';
const SUPPORTED_REGIONS = new Set(['ru', 'eu']);
const SUPPORTED_LOCALES = new Set(
  (process.env.VUE_APP_I18N_SUPPORTED_LOCALE || 'uk,en,ru,es,pt')
    .split(',')
    .map((locale) => locale.trim().toLowerCase())
    .filter(Boolean),
);

// Откуда взят регион: `query` — явный `?region=` (его можно запомнить), `config` — настройка
// стенда, `locale` — только если стенд регион не настраивает.
const RegionSource = Object.freeze({ QUERY: 'query', CONFIG: 'config', LOCALE: 'locale' });

function createInitialContext() {
  return {
    partnerId: '',
    region: '',
    regionSource: '',
  };
}

function normalizeQueryValue(value) {
  const normalizedValue = Array.isArray(value) ? value[0] : value;
  return typeof normalizedValue === 'string' ? normalizedValue.trim() : '';
}

function normalizeRegion(value) {
  const region = normalizeQueryValue(value).toLowerCase();
  return SUPPORTED_REGIONS.has(region) ? region : '';
}

function regionFromLocale(value) {
  const locale = normalizeQueryValue(value).toLowerCase();
  if (!SUPPORTED_LOCALES.has(locale)) return '';

  return locale === 'ru' ? 'ru' : 'eu';
}

// Регион определяет хранилище аккаунтов (auth/onboarding), а не язык интерфейса:
// русскоязычный UI на EU-стенде обязан регистрироваться в EU. Поэтому настройка стенда
// (`VUE_APP_ONBOARDING_DEFAULT_REGION`, затем `VUE_APP_AUTH_REGION`) важнее locale.
export function getConfiguredRegion() {
  return normalizeRegion(process.env.VUE_APP_ONBOARDING_DEFAULT_REGION)
    || normalizeRegion(process.env.VUE_APP_AUTH_REGION);
}

const referralContext = useStorage(
  STORAGE_KEY,
  createInitialContext(),
  sessionStorage,
  { mergeDefaults: true },
);

export function usePartnerReferralContext() {
  // Приоритет региона: валидный явный `?region=` → ранее сохранённый явный регион →
  // регион стенда → locale (только если стенд регион не настраивает) → `ru`.
  // Регион, выведенный не из запроса (config/locale), не запоминается как «явный»,
  // поэтому устаревший контекст из другой конфигурации не перебивает настройку стенда.
  function resolveRegion(query, locale) {
    const queryRegion = normalizeRegion(query.region);
    if (queryRegion) return { region: queryRegion, regionSource: RegionSource.QUERY };

    const rememberedRegion = referralContext.value.regionSource === RegionSource.QUERY
      ? normalizeRegion(referralContext.value.region)
      : '';
    if (rememberedRegion) return { region: rememberedRegion, regionSource: RegionSource.QUERY };

    const configuredRegion = getConfiguredRegion();
    if (configuredRegion) return { region: configuredRegion, regionSource: RegionSource.CONFIG };

    return {
      region: regionFromLocale(locale) || 'ru',
      regionSource: RegionSource.LOCALE,
    };
  }

  function captureFromQuery(query = {}, locale = '') {
    const partnerId = normalizeQueryValue(query.partnerId);

    referralContext.value = {
      partnerId: partnerId || referralContext.value.partnerId,
      ...resolveRegion(query, locale),
    };
  }

  function resetReferralContext() {
    referralContext.value = createInitialContext();
  }

  return {
    referralContext: readonly(referralContext),
    captureFromQuery,
    resetReferralContext,
  };
}
